import React, { startTransition } from "react";
import { useDrag } from "react-dnd";
import styled from "styled-components";

import { colourStaticGrey } from "@ingka/variables/colours-css";
import Badge from "@ingka/badge";
import Tooltip from "@ingka/tooltip";

import arrowUpArrowDown from "@ingka/ssr-icon/paths/arrow-up-arrow-down";
import search from "@ingka/ssr-icon/paths/magnifying-glass";
import Button from "@ingka/button";

import { DndItems } from "../../core/dnd";
import { SchemaIcon } from "../../core/icons";
import {
	getChildren,
	getLabel,
	getPath,
	isArrayElement,
	isObjectElement,
	SchemaElement,
} from "../../core/model/schema";
import { EditorUISchemaElement } from "../../core/model/uischema";
import { createControl } from "../../core/util/generators/uiSchema";
import { StyledTreeItem, StyledTreeView } from "./Tree";
import { useSearchElement, useSearchElementParents } from "../../core/context";
import { PillDropdown } from "../../../Dropdown";
import { MenuItem } from "@ingka/list-box";

interface SchemaTreeItemProps {
	schemaElement: SchemaElement;
}

const StyledDropdown = styled(PillDropdown)`
	.list-box {
		width: 10rem !important;
	}
`;

const LabelWrapper = styled.div`
	display: flex;
	align-items: center;
	margin: 0.15rem 0 0 0;
`;

const Paragraph = styled.p`
	margin: 0;
	flex-grow: 1;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	margin-right: 3rem;
`;

const StyledBadgeWrapper = styled.div`
	position: fixed;
	right: 0;
	margin-bottom: 0.4rem;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.2rem;
`;

const SchemaTreeItem: React.FC<SchemaTreeItemProps> = ({ schemaElement }) => {
	const uiSchemaElement: EditorUISchemaElement = createControl(schemaElement);
	const [, setSearchElement] = useSearchElement();
	const [, setSearchElementParent] = useSearchElementParents();

	//@ts-ignore
	const scopeSplit = uiSchemaElement?.scope?.split("/") || null;
	const isAddressOrder = scopeSplit ? scopeSplit[scopeSplit.length - 1] === "addressOrder" : false;

	const [{ isDragging }, drag] = useDrag({
		type: DndItems.newUISchemaElement(uiSchemaElement, schemaElement.uuid).type,
		item: DndItems.newUISchemaElement(uiSchemaElement, schemaElement.uuid),
		canDrag: () => {
			return schemaElement.schema.type !== "object" || isAddressOrder;
		},
		collect: (monitor) => ({
			isDragging: !!monitor.isDragging(),
		}),
	});

	React.useEffect(() => {
		if (isDragging) {
			setSearchElement(undefined);
			setSearchElementParent([]);
		}
	}, [isDragging, setSearchElement, setSearchElementParent]);

	const schemaElementPath = getPath(schemaElement);

	return (
		<div ref={drag} data-cy={`${schemaElementPath}-source`} style={{ opacity: 0.95 }}>
			<StyledTreeItem
				itemId={schemaElementPath}
				label={
					<LabelWrapper>
						<Tooltip tooltipText={schemaElement.type === "other" ? "select" : schemaElement.type}>
							<span style={{ display: "inline-flex", marginRight: "0.5rem" }}>
								<SchemaIcon type={schemaElement.type} />
							</span>
						</Tooltip>

						<Paragraph>{getLabel(schemaElement)}</Paragraph>

						{schemaElement.linkedUISchemaElements?.size !== undefined &&
							schemaElement.linkedUISchemaElements?.size !== 0 && (
								<StyledBadgeWrapper data-cy={`${schemaElementPath}-count`}>
									<Tooltip position="leading" tooltipText={"Find property"}>
										<Button
											data-cy={`${schemaElementPath}-button`}
											ssrIcon={search}
											size="xsmall"
											iconOnly
											type="tertiary"
											onClick={() => {
												if (schemaElement.type !== "object") {
													setSearchElement(undefined);
													setSearchElementParent([]);
													startTransition(() => {
														const searchingEl = new Set(schemaElement.linkedUISchemaElements);
														schemaElement?.uuid && searchingEl?.add(schemaElement.uuid);
														schemaElement?.uuid && setSearchElement({ uuid: schemaElement.uuid });
													});
												}
											}}
										/>
									</Tooltip>
									<Tooltip
										data-cy={`${schemaElementPath}-tooltip`}
										position="leading"
										tooltipText={`Property used: ${schemaElement.linkedUISchemaElements.size.toString()} time(s)`}
									>
										<Badge colour="grey" label={schemaElement.linkedUISchemaElements.size.toString()} size="small" />
									</Tooltip>
								</StyledBadgeWrapper>
							)}
					</LabelWrapper>
				}
				isDragging={isDragging}
			>
				{schemaElement.type !== "other" &&
					!isAddressOrder &&
					getChildrenToRender(schemaElement).map((child) => (
						<SchemaTreeItem schemaElement={child} key={getPath(child)} />
					))}
			</StyledTreeItem>
		</div>
	);
};

const getChildrenToRender = (schemaElement: SchemaElement) => {
	return getChildren(schemaElement).flatMap((child) => {
		if (isObjectElement(child) && isArrayElement(child.parent) && child.parent.items === child) {
			return getChildren(child);
		}
		return [child];
	});
};

const HeadingWrapper = styled.div`
	display: flex;
	padding-left: 1rem;
	margin-bottom: 0.5rem;
	border-bottom: 1px solid ${colourStaticGrey};
	border-top: 1px solid ${colourStaticGrey};
	align-items: center;
	gap: 0.5rem;

	.list-box {
		width: 10rem !important;
	}
`;

const StyledH3 = styled.h3`
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
`;

type SortingUISchema = "name" | "type";

export const SchemaTreeView: React.FC<{
	schema: SchemaElement | undefined;
	sortingUISchema: SortingUISchema;
	setSortingUISchema: (sorting: SortingUISchema) => void;
	hidePalettePanel: boolean;
}> = ({ schema, hidePalettePanel, sortingUISchema, setSortingUISchema }) => {
	return (
		<>
			{!hidePalettePanel && (
				<>
					<HeadingWrapper>
						<StyledH3>Controls</StyledH3>
						<StyledDropdown
							id="sorting-uischema-btn"
							key="sorting-uischema-btn"
							ssrIcon={arrowUpArrowDown}
							size="xsmall"
							iconOnly
							className={"marginRight: 0.5rem"}
							ariaLabel="Sorting"
							onChange={(value: SortingUISchema) => {
								setSortingUISchema(value);
							}}
							value={sortingUISchema}
						>
							<MenuItem data-testid="sortingByNameOption" id="name" key="sorting-name-option" title="By Name" />
							<MenuItem data-testid="sortingByTypeOption" id="type" key="sorting-type-option" title="By Type" />
						</StyledDropdown>
					</HeadingWrapper>

					{schema !== undefined ? (
						<StyledTreeView defaultExpandedItems={[getPath(schema)]}>
							<SchemaTreeItem schemaElement={schema} />
						</StyledTreeView>
					) : (
						<NoSchema />
					)}
				</>
			)}
		</>
	);
};

const NoSchema = () => <div>No JSON Schema available</div>;
